import * as React from 'react';

const IFRAME_FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(container: HTMLDivElement): HTMLElement[] {
  const ownerDocument = container.ownerDocument;
  const HTMLElementConstructor = ownerDocument.defaultView?.HTMLElement ?? HTMLElement;

  return Array.from(
    container.querySelectorAll<HTMLElement>(IFRAME_FOCUSABLE_SELECTOR),
  ).filter(element => {
    if (!(element instanceof HTMLElementConstructor)) {
      return false;
    }

    if (element.tabIndex < 0 || element.hidden || element.getAttribute('aria-hidden')) {
      return false;
    }

    return element.getClientRects().length > 0 || element === ownerDocument.activeElement;
  });
}

/**
 * Radix's focus scope binds auto-focus to the top-level `document`. Inside the SDK iframe
 * (`window.chekinCustomDocument`) that document's `activeElement` is always the `<iframe>`,
 * so the overlay's mount auto-focus bounces between the first field and the content container,
 * leaving searchable Selects stuck half-focused. Preventing the open auto-focus only inside the
 * custom document stops the bounce; Tab trapping stays handled by useIframeFocusTrapFallback.
 */
export function useIframeOpenAutoFocusGuard(onOpenAutoFocus?: (event: Event) => void) {
  return React.useCallback(
    (event: Event) => {
      onOpenAutoFocus?.(event);

      if (event.defaultPrevented) {
        return;
      }

      if (typeof window !== 'undefined' && window.chekinCustomDocument) {
        event.preventDefault();
      }
    },
    [onOpenAutoFocus],
  );
}

export function useIframeFocusTrapFallback(
  contentRef: React.RefObject<HTMLDivElement | null>,
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>,
) {
  return React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);

      if (event.defaultPrevented || event.key !== 'Tab') {
        return;
      }

      const contentNode = contentRef.current;

      if (
        !contentNode ||
        !window.chekinCustomDocument ||
        contentNode.ownerDocument !== window.chekinCustomDocument
      ) {
        return;
      }

      const focusableElements = getFocusableElements(contentNode);

      if (!focusableElements.length) {
        event.preventDefault();
        contentNode.focus();
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];
      const activeElement = contentNode.ownerDocument.activeElement;

      if (focusableElements.length === 1) {
        event.preventDefault();
        firstFocusableElement.focus();
        return;
      }

      if (event.shiftKey) {
        if (activeElement === firstFocusableElement || activeElement === contentNode) {
          event.preventDefault();
          lastFocusableElement.focus();
        }

        return;
      }

      if (activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    },
    [contentRef, onKeyDown],
  );
}
